import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Form, Badge, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import CustomModal from '../componentes/Modal';
import FormularioContenido from '../componentes/FormularioContenido';
import Alerta from '../componentes/Alerta';
import { crearContenido, editarContenido, eliminarContenido } from '../servicios/crud';
import { obtenerContenidos } from '../servicios/consultas';
import '../componentes/UserManagement.css';

const ContentManagement = ({ currentUser }) => {
  const [contenidos, setContenidos] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [alerta, setAlerta] = useState(null);
  const [editando, setEditando] = useState(null);
  const [cargando, setCargando] = useState(false);

  
const mostrarAlerta = useCallback((tipo, titulo, contenido, onConfirm) => {
  setAlerta({ tipo, titulo, contenido, onConfirm }); // Guardar el callback
}, []);
  
  const cargarContenidos = useCallback(async () => {
    try {
      const datos = await obtenerContenidos();
      setContenidos(datos);
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    }
  }, [mostrarAlerta]);

  useEffect(() => {
    cargarContenidos();
  }, [cargarContenidos]);

  // ========== OPERACIONES CRUD ==========
  const manejarCreacionContenido = async (contenidoData) => {
    try {
      setCargando(true);
  
      // Validar enlaces internos
      contenidoData.contenido.forEach(tarjeta => {
        if (tarjeta.tipo === 'interno' && !tarjeta.enlace.startsWith('/')) {
          throw new Error('Enlaces internos deben comenzar con "/"');
        }
      });
  
      // Validar formato de código
      if (!/^[A-Z]{3}-\d{6}$/.test(contenidoData.código)) {
        throw new Error('Formato de código inválido. Debe ser: AAA-999999');
      }
  
      await crearContenido(contenidoData);
      mostrarAlerta('success', 'Éxito', 'Contenido creado correctamente');
      setModalAbierto(false);
      await cargarContenidos();
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    } finally {
      setCargando(false);
    }
  };
  
  const manejarEdicionContenido = async (datos) => {
    try {
      setCargando(true);
  
      // Validar enlaces internos
      datos.contenido.forEach(tarjeta => {
        if (tarjeta.tipo === 'interno' && !tarjeta.enlace.startsWith('/')) {
          throw new Error('Enlaces internos deben comenzar con "/"');
        }
      });
  
      await editarContenido(editando.id, datos);
      mostrarAlerta('success', 'Éxito', 'Contenido actualizado correctamente');
      await cargarContenidos();
      setEditando(null);
    } catch (error) {
      mostrarAlerta('error', 'Error', error.message);
    } finally {
      setCargando(false);
    }
  };
  
  const manejarEliminacion = useCallback(async (id) => {
    if (currentUser?.rol !== "superadmin") {
      mostrarAlerta("error", "Permiso denegado", "Solo superadmins pueden eliminar");
      return;
    }
  
    try {
      await eliminarContenido(id);
      mostrarAlerta("success", "Éxito", "Contenido eliminado");
      await cargarContenidos();
    } catch (error) {
      mostrarAlerta("error", "Error", error.message);
    }
  }, [mostrarAlerta, cargarContenidos, currentUser?.rol]); // ✅ Dependencias
  
 // ==========  ==========
  const columnas = useMemo(() => [
    {
      id: 'curso',
      name: 'Curso',
      selector: row => row.curso,
      sortable: true,
      wrap: true,
      minWidth: '220px',
      style: {
        fontWeight: '600',
        color: '#273b47'
      }
    },
    {
      name: 'Código',
      selector: row => row.código || 'N/A', // Manejo de valores undefined
      sortable: true,
      maxWidth: '150px',
      omit: ['sm', 'md'] // Corregido: usar 'omit' en lugar de 'hide'
    },
    {
      id: 'estado',
      name: 'Estado',
      selector: row => row.estado,
      sortable: true,
      cell: row => (
        <Badge pill bg={row.estado?.toLowerCase() === 'activo' ? 'success' : 'secondary'}>
          {row.estado || 'Desconocido'}
        </Badge>
      ),
      maxWidth: '120px'
    },
    {
      id:"modulos",
      name: 'Módulos',
      selector: row => row.contenido?.length || 0,
      cell: row => (
        <Badge pill bg="primary">
          {Array.isArray(row.contenido) ? row.contenido.length : 0}
        </Badge>
      ),
      maxWidth: '100px',
      omit: false, // Mostrar en todos los tamaños de pantalla
    },
    {
      name: 'Creación',
      selector: row => row.fechaCreacion?.seconds 
        ? new Date(row.fechaCreacion.seconds * 1000).toLocaleDateString() 
        : 'N/A',
      sortable: true,
      maxWidth: '150px',
      omit: ['lg', 'xl']
    },
    {
      id: 'acciones',
      name: 'Acciones',
      cell: row => (
        <div className="d-none d-lg-flex gap-2">
           <Button
          variant="outline-primary"
          onClick={() => setEditando(row)}
          className="table-btn table-edit-btn"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
              <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
              <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
            </svg>
          </Button>
          <Button
          variant="outline-danger"
          onClick={() => mostrarAlerta(
            'warning',
            'Confirmar',
            '¿Eliminar contenido permanentemente?',
            () => manejarEliminacion(row.id)
          )}
          className="table-btn table-delete-btn"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash3-fill" viewBox="0 0 16 16">
              <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
            </svg>
          </Button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      minWidth: '100px',
      button: true,
      hide: 'md' // Ocultar en pantallas menores a lg
    }
  ], [manejarEliminacion]); // ✅ Ahora es estable

  const ExpandableComponent = ({ data }) => (
    <div className="p-3 bg-light" style={{ marginTop: '-10px' }}>
      <div className="mb-2">
        <strong>Código:</strong> {data.código}
      </div>
      <div className="mb-2">
        <strong>Fecha Creación:</strong> {new Date(data.fechaCreacion?.seconds * 1000).toLocaleDateString()}
      </div>

      <div className="mb-2">
  <strong>Módulos:</strong> 
  <Badge pill bg="primary">
    {Array.isArray(data.contenido) ? data.contenido.length : 0}
  </Badge>
</div>
      <div className="d-lg-none mt-2">
        <Button
          variant="outline-primary"
          onClick={() => setEditando(data)}
          className="table-btn table-edit-btn w-100 mb-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-pencil-square me-1" viewBox="0 0 16 16">
            <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
            <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"/>
          </svg>
          Editar
        </Button>
        <Button
  variant="outline-danger"
  onClick={() => mostrarAlerta(
    'warning',
    'Confirmar',
    '¿Eliminar contenido permanentemente?',
    () => manejarEliminacion(data.id) // <- Añadir esta acción
  )}
  className="table-btn table-delete-btn w-100"
>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-trash3-fill me-1" viewBox="0 0 16 16">
            <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
          </svg>
          Eliminar
        </Button>
      </div>
    </div>
  );

  const TablaEstilizada = styled(DataTable)`
    border-radius: 15px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

    .rdt_TableHead {
      background-color: #273b47 !important;
      > div {
        background-color: transparent !important;
        padding: 0 !important;
      }
    }

    @media (max-width: 768px) {
      .rdt_TableHeadRow {
        border-top-left-radius: 15px !important;
        border-top-right-radius: 15px !important;
      }
    }
  `;

  const contenidosFiltrados = useMemo(() => {
    if (!filterText) return contenidos;
    const texto = filterText.toLowerCase();
    return contenidos.filter(contenido => 
      contenido.código.toLowerCase().includes(texto) ||
      contenido.curso.toLowerCase().includes(texto)
    );
  }, [contenidos, filterText]);

  return (
    <div className="content-management">
     {alerta && (
  <Alerta
    type={alerta.tipo}
    title={alerta.titulo}
    content={alerta.contenido}
    onConfirm={() => {
      // ✅ Ejecutar el callback de eliminación si existe
      if (alerta.onConfirm) alerta.onConfirm();
      setAlerta(null); // Cerrar la alerta
    }}
  />
)}
  
      <div className="tab-content">
        <div className="header-section d-flex justify-content-between align-items-center mb-4">
          <h2 className="m-0" style={{ color: '#273b47' }}>
            Gestión de Contenidos ({contenidos.length})
          </h2>
          <Button
            onClick={() => setModalAbierto(true)}
            className="custom-btn primary"
            disabled={cargando}
            style={{ backgroundColor: '#98ca3f', border: 'none' }}
          >
            {cargando ? <Spinner size="sm" /> : '+ Nuevo Contenido'}
          </Button>
        </div>
  
        <TablaEstilizada
          columns={columnas}
          data={contenidosFiltrados}
          pagination
          responsive
          highlightOnHover
          expandableRows
          expandableRowsComponent={ExpandableComponent}
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 30]}
          subHeader
          subHeaderComponent={
            <Form.Control
              type="text"
              placeholder="🔍 Buscar por curso o código..."
              onChange={e => setFilterText(e.target.value)}
              value={filterText}
              className="custom-search-input mb-3"
            />
          }
          customStyles={{
            headCells: {
              style: {
                backgroundColor: '#273b47',
                color: '#f3f8fb',
                fontSize: '0.95rem',
              },
            },
            cells: {
              style: {
                color: '#7e96a5',
                fontSize: '0.9rem',
              },
            },
          }}
        />
  
        <CustomModal
          show={modalAbierto}
          handleClose={() => setModalAbierto(false)}
          title="Nuevo Contenido"
        >
          <FormularioContenido 
            onSubmit={manejarCreacionContenido}
            onCancel={() => setModalAbierto(false)}
            disabled={cargando}
          />
        </CustomModal>
  
        <CustomModal
  show={!!editando}
  handleClose={() => setEditando(null)}
  title="Editar Contenido"
>
  <FormularioContenido 
    onSubmit={manejarEdicionContenido}
    onCancel={() => setEditando(null)}
    initialData={editando}
    disabled={cargando}
  />
</CustomModal>
      </div>
    </div>
  );
};

export default ContentManagement;